import type { KeyboardEvent } from 'react';
import dayjs from 'dayjs';

import type {
  AppointmentHistoryDraft,
  AppointmentHistoryEntry,
  AppointmentHistoryStaff,
  AppointmentServiceLine,
} from '@erp/appointments/api/appointments.api';
import type { ClinicalRecord } from '@erp/clinical-history/api/clinicalHistory.api';
import type { StaffDirectoryEntry } from '@erp/services/api/serviceStaff';
import { normalizeRichTextHtml, truncateRichTextPlainText } from '@tools/utils/richTextHtml.ts';

export interface StaffOption {
  value: number;
  label: string;
}

export interface HistoryFormValues {
  staff_user_id?: number;
  duration_minutes?: number | null;
  notes?: string | null;
  sale_item_id?: number | null;
  follow_up_appointment_id?: number | null;
}

export const buildStaffDirectoryLabel = (staff: StaffDirectoryEntry): string => {
  const fullName = [staff.first_name, staff.last_name]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(' ');

  return fullName || staff.username || `Staff #${staff.id}`;
};

export const buildHistoryStaffLabel = (
  staffId: number,
  staffDirectory: StaffDirectoryEntry[],
  entry?: Pick<AppointmentHistoryEntry, 'staff'>,
): string => {
  const staff = staffDirectory.find((item) => item.id === staffId);
  if (staff) {
    return buildStaffDirectoryLabel(staff);
  }

  const entryFullName = entry?.staff
    ? [entry.staff.first_name, entry.staff.last_name]
        .filter((part): part is string => Boolean(part?.trim()))
        .join(' ')
    : '';

  return entryFullName || entry?.staff?.username || `Staff #${staffId}`;
};

export const buildStaffOptions = (staffDirectory: StaffDirectoryEntry[]): StaffOption[] =>
  staffDirectory.map((staff) => ({
    value: staff.id,
    label: buildStaffDirectoryLabel(staff),
  }));

export const mergeStaffOptions = (
  baseOptions: StaffOption[],
  extraOption: StaffOption | null,
): StaffOption[] => {
  if (!extraOption || baseOptions.some((option) => option.value === extraOption.value)) {
    return baseOptions;
  }
  return [...baseOptions, extraOption];
};

export const buildHistoryDraft = (values: HistoryFormValues): AppointmentHistoryDraft => ({
  staff_user_id: values.staff_user_id!,
  duration_minutes: values.duration_minutes ?? undefined,
  notes: normalizeRichTextHtml(values.notes),
  sale_item_id: values.sale_item_id ?? undefined,
  follow_up_appointment_id: values.follow_up_appointment_id ?? undefined,
});

const splitStaffName = (staffName: string | null): Pick<AppointmentHistoryStaff, 'first_name' | 'last_name'> => {
  const [firstName, ...lastNameParts] = staffName?.trim().split(/\s+/).filter(Boolean) ?? [];
  return {
    first_name: firstName ?? null,
    last_name: lastNameParts.length > 0 ? lastNameParts.join(' ') : null,
  };
};

export const clinicalRecordToHistoryEntry = (record: ClinicalRecord): AppointmentHistoryEntry => ({
  id: record.latest_history_entry_id!,
  appointment_id: record.id,
  staff_user_id: record.staff_user_id,
  duration_minutes: null,
  notes: record.latest_history_note,
  sale_item_id: null,
  follow_up_appointment_id: null,
  created_at: record.created_at,
  created_by: null,
  staff: {
    id: record.staff_user_id,
    username: null,
    ...splitStaffName(record.staff_name),
  },
  sale_item: null,
  follow_up: null,
});

export const buildClinicalRecordSpaceLabel = (
  record: ClinicalRecord,
  spaceNameById?: Map<number, string>,
): string => record.space_name ?? spaceNameById?.get(record.space_id) ?? `Espacio #${record.space_id}`;

export const buildCompactClinicalRecordSpaceLabel = (
  record: ClinicalRecord,
  spaceNameById?: Map<number, string>,
): string => record.space_name ?? spaceNameById?.get(record.space_id) ?? `Esp.#${record.space_id}`;

export const buildClinicalRecordStaffLabel = (record: ClinicalRecord): string =>
  record.staff_name ?? `Staff #${record.staff_user_id}`;

export const buildServiceLinesLabel = (serviceLines: AppointmentServiceLine[]): string =>
  serviceLines
    .map((line) => line.service_name ?? `Serv.#${line.service_id}`)
    .filter((label) => label.trim().length > 0)
    .join(', ');

export const hasClinicalHistoryNote = (record: ClinicalRecord): boolean =>
  record.history_entries_count > 0 && Boolean(normalizeRichTextHtml(record.latest_history_note));

export const buildClinicalRecordDateLabel = (record: ClinicalRecord): string =>
  dayjs(record.start_at).format('DD/MM HH:mm');

export const buildClinicalRecordNotePreview = (record: ClinicalRecord): string =>
  truncateRichTextPlainText(record.latest_history_note, 120);

export const createActivationKeyDown =
  (onActivate: () => void) =>
  (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate();
    }
  };

export const buildEntryInitialValues = (entry: AppointmentHistoryEntry): HistoryFormValues => ({
  staff_user_id: entry.staff_user_id,
  duration_minutes: entry.duration_minutes ?? undefined,
  notes: entry.notes ?? undefined,
  sale_item_id: entry.sale_item_id ?? undefined,
  follow_up_appointment_id: entry.follow_up_appointment_id ?? undefined,
});
